import { useBreakpoint } from '@/common/hooks/use-breakpoint';
import { scrollToRef } from '@/common/utils/scroll-to-ref';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

/**
 * Rect y value is distance between top of viewport to top of element, so we need to add height to get bottom edge y position.
 */
const getBottomEdgeOfRect = (rect: DOMRect): number => rect.y + rect.height;

const sectionKeyToIndexMapping = {
  features: 0,
  savings: 1,
  whyFlexcar: 2,
  inspection: 3,
};
export type SectionKey = keyof typeof sectionKeyToIndexMapping;

export interface UseFloatyBarResponse {
  /* Ref to continue button on plan selection panel */
  continueButtonRef: (node?: Element | null) => void;
  /* Flag to show floaty bar */
  showFloatyBar: boolean;
  /* Which section is currently considered "in view". If multiple sections are in view, the top-most section is returned. */
  currentSection: SectionKey | null;
  /* Callback to update section in view state for a particular section */
  onInViewChange: (inView: boolean, sectionKey: SectionKey) => void;
  /* Refs of the sections on the floaty bar */
  sectionRefs: React.RefObject<Record<SectionKey, HTMLDivElement | null>>;
  /* Callback to add a section ref to sectionRefs */
  addSectionRef: (node: HTMLDivElement, sectionKey: SectionKey) => void;
  /* Callback that scrolls to the savings section specifically */
  scrollToSavings: () => void;
}

export default function useFloatyBar(): UseFloatyBarResponse {
  const breakpoint = useBreakpoint();
  const refCopy = useRef<HTMLButtonElement>(null);
  const { ref, inView } = useInView();
  const [sectionInViews, setSectionInViews] = useState([false, false, false, false]);
  const sectionRefs = useRef<Record<SectionKey, HTMLDivElement | null>>({
    features: null,
    savings: null,
    whyFlexcar: null,
    inspection: null,
  });

  const targetIsAboveViewport =
    refCopy.current !== null &&
    getBottomEdgeOfRect(refCopy.current.getBoundingClientRect()) <= 0;

  const refWrapper = (node?: Element | null): void => {
    refCopy.current = node as HTMLButtonElement;
    ref(node);
  };

  const currentSection = useMemo(() => {
    /**
     * Return the top-most section that is in view, or null if none are in view.
     *
     * e.g. If both features and savings are in view, return features because the features section is above the savings section.
     */
    const indexOfFirstSectionInView = sectionInViews.indexOf(true);
    const currentSectionKey = Object.keys(sectionKeyToIndexMapping).reduce(
      (acc, sectionKey) =>
        sectionKeyToIndexMapping[sectionKey as SectionKey] === indexOfFirstSectionInView
          ? sectionKey
          : acc,
      ''
    );
    return currentSectionKey ? (currentSectionKey as SectionKey) : null;
  }, [sectionInViews]);

  const onInViewChange = useCallback((inView: boolean, sectionKey: SectionKey) => {
    setSectionInViews((prev) => {
      const updatedSectionInViews = [...prev];
      updatedSectionInViews[sectionKeyToIndexMapping[sectionKey]] = inView;
      return updatedSectionInViews;
    });
  }, []);

  const addSectionRef = useCallback((node: HTMLDivElement, sectionKey: SectionKey) => {
    sectionRefs.current[sectionKey] = node;
  }, []);

  const scrollToSavings = useCallback(() => {
    /**
     * Logic behind this offset:
     * - 40px top on floaty bar
     * - 80px height of floaty bar
     *
     * So that leaves approximately 80px (200px - 120px) between floaty bar and ref we scroll to.
     */
    scrollToRef({ current: sectionRefs.current.savings }, 'top', 200);
  }, []);

  return {
    continueButtonRef: refWrapper,
    showFloatyBar: !inView && targetIsAboveViewport && breakpoint === 'desktop',
    currentSection,
    onInViewChange,
    sectionRefs,
    addSectionRef,
    scrollToSavings,
  };
}
